"use client";

import React, { ReactNode } from "react";
import { Button, Collapsible } from "@chakra-ui/react";
import GenericIcon, {
  type GenericIconType,
  type ResponsiveIconSize,
} from "../actions/GenericIcon";

export type GenericCollapsibleButtonProps = {
  isOpen?: boolean;
  setIsOpenAction?: (open: boolean) => void;
  buttonVariant?: "ghost" | "outline" | "solid";
  icon: GenericIconType;
  iconSize?: ResponsiveIconSize;
};

function getCollapsibleContentProps(setChildrenBorder: boolean) {
  if (!setChildrenBorder) {
    return {};
  }

  return {
    p: "4",
    rounded: "md",
    borderWidth: "1",
    borderColor: "moveon.surface.border",
  } as const;
}

export function GenericCollapsibleButton({
  isOpen,
  setIsOpenAction,
  buttonVariant = "ghost",
  icon,
  iconSize,
}: GenericCollapsibleButtonProps) {
  return (
    <Button
      type="button"
      variant={buttonVariant}
      onClick={() => setIsOpenAction && setIsOpenAction(!isOpen)}
    >
      <GenericIcon icon={icon} size={iconSize} />
    </Button>
  );
}

export type GenericCollapsibleProps = {
  icon?: GenericIconType;
  iconSize?: ResponsiveIconSize;
  defaultOpen?: boolean;
  isOpen?: boolean;
  setIsOpenAction?: (open: boolean) => void;
  setChildrenBorder?: boolean;
  children: ReactNode;
};

export default function GenericCollapsible({
  icon = "settings",
  iconSize,
  defaultOpen = false,
  isOpen, // optional controlled component (call GenericCollapsibleButton alongside)
  setIsOpenAction, // optional controlled component (called by GenericCollapsibleButton)
  setChildrenBorder = false,
  children,
}: GenericCollapsibleProps) {
  const collapsibleContentProps = getCollapsibleContentProps(setChildrenBorder);

  if (isOpen !== undefined && setIsOpenAction) {
    return (
      <Collapsible.Root
        width="full"
        open={isOpen}
        onOpenChange={(e) => setIsOpenAction(e.open)}
        unmountOnExit={true}
        lazyMount={true}
      >
        <Collapsible.Content {...collapsibleContentProps}>
          {children}
        </Collapsible.Content>
      </Collapsible.Root>
    );
  }

  return (
    <Collapsible.Root
      width="full"
      defaultOpen={defaultOpen}
      unmountOnExit={false}
    >
      <Collapsible.Trigger>
        <GenericIcon icon={icon} size={iconSize} />
      </Collapsible.Trigger>
      <Collapsible.Content {...collapsibleContentProps}>
        {children}
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
